import { gql, useMutation } from '@apollo/client';
import { Link } from 'gatsby';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Header from '@ui/atoms/Header';
import Button from '@ui/atoms/Ws-Button';

import TextInput from '@ui/forms/TextInput';

import Wordmark from '@assets/vectors/wordmark.inline.svg';

import theme from '@theme/WsTheme';

import { ICurrentMember, PageWrapper } from './ZendeskSSO.component';

const LogoLink = styled(Link)`
	display: flex;
	justify-content: center;
`;

const UpdateNameComponent: FC<{
	handleNameUpdated: () => unknown;
	currentMember?: ICurrentMember;
}> = ({ handleNameUpdated, currentMember }) => {
	const [loading, setLoading] = useState(false);
	interface INameFormFields {
		affirmingFirstName: string;
		affirmingLastName: string;
	}

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<INameFormFields>();

	// Mutation Query
	const MUTATION_QUERY = gql`
		mutation UpdateMemberName($clientMutationId: String, $memberId: UUID!, $patch: MemberPatch!) {
			updateMember(input: { clientMutationId: $clientMutationId, id: $memberId, patch: $patch }) {
				clientMutationId
				member {
					id
					affirmingFirstName
					affirmingLastName
				}
			}
		}
	`;

	const [updateMemberName] = useMutation<any>(MUTATION_QUERY);

	// Styles

	const SignInFormContainer = styled.div`
		background: ${theme.colors.lightGray};
		padding: ${theme.spacing.md};
		display: flex;
		flex-flow: column;
		align-content: center;
		margin: 0 ${theme.spacing.md};

		${Header} {
			margin-bottom: ${theme.spacing.sm};

			@media ${theme.responsive.breakpoints.tablet} {
				font-size: ${theme.fontSizes.header.lg};
				line-height: ${theme.lineHeights.header.xl};
			}
		}

		${Button} {
			width: 100%;
		}

		@media ${theme.responsive.breakpoints.tablet} {
			width: 50%;
			margin: 0 auto;
		}
	`;

	const LogoContainer = styled.div`
		display: flex;
		justify-content: center;
		align-content: center;
		padding: ${theme.spacing.lg} 0 ${theme.spacing.lg} 0;
		min-height: 4rem;
	`;

	const FormWrapper = styled.div`
		display: flex;
		flex-flow: column;
		margin-top: ${theme.spacing.md};
		margin-bottom: ${theme.spacing.md};
		gap: ${theme.spacing.md};
	`;

	const updateNames = async ({ affirmingFirstName, affirmingLastName }: INameFormFields) => {
		try {
			setLoading(true);
			await updateMemberName({
				variables: {
					clientMutationId: currentMember?.id,
					memberId: currentMember?.id,
					patch: {
						affirmingFirstName: affirmingFirstName.trim(),
						affirmingLastName: affirmingLastName.trim(),
					},
				},
			});
			handleNameUpdated();
		} catch (err) {
			setLoading(false);
			console.error(err);
			setError('affirmingLastName', {
				message: 'An unknown error occurred!',
				type: 'LOGIN_FAILED',
			});
		}
	};

	return (
		<PageWrapper className="login-page">
			<LogoContainer>
				<LogoLink to="/">
					<Wordmark />
				</LogoLink>
			</LogoContainer>
			<SignInFormContainer>
				<header>
					<Header level="4" as="h1">
						Enter your preferred name
					</Header>
				</header>
				<form onSubmit={handleSubmit(updateNames)}>
					<FormWrapper>
						<TextInput
							label="First name"
							{...register('affirmingFirstName', {
								required: true,
								minLength: 1,
								maxLength: 50,
							})}
							fullWidth
						/>
						<TextInput
							label="Last name"
							{...register('affirmingLastName', {
								required: true,
								minLength: 1,
								maxLength: 50,
							})}
							fullWidth
						/>
					</FormWrapper>
					<Button type="submit" label="Sign In" disabled={loading} />
				</form>
			</SignInFormContainer>
		</PageWrapper>
	);
};

export default UpdateNameComponent;
